// SuperAdminConsole — Phase J of the Client-Surfaces Programme.
// Cross-tenant operator view (different from per-org admin). List all orgs,
// drill into any, and set per-tenant branding tokens (theming hook).
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Building2, ChevronRight, Loader2, Save, Shield, Palette, Users, MapPin
} from 'lucide-react';
import { apiBase } from '../utils/authHeaders';
import { AccessDeniedPage } from './RouteStates';

const API_URL = apiBase();

const SuperAdminConsole = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [branding, setBranding] = useState({ primary_color: '', accent_color: '', logo_url: '', display_name: '' });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token]);

  const loadOrgs = useCallback(async () => {
    setLoading(true); setError(null); setForbidden(false);
    try {
      const res = await fetch(`${API_URL}/api/superadmin/orgs`, { headers });
      if (res.status === 403) {
        setForbidden(true);
        setOrgs([]);
        return;
      }
      if (!res.ok) throw new Error(res.statusText);
      setOrgs(await res.json());
    } catch (e) { setError(String(e)); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const openOrg = async (orgId) => {
    setSelectedId(orgId);
    setDetail(null);
    setSavedAt(null);
    try {
      const res = await fetch(`${API_URL}/api/superadmin/orgs/${orgId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDetail(data);
        const b = data?.config?.branding || {};
        setBranding({
          primary_color: b.primary_color || '',
          accent_color:  b.accent_color  || '',
          logo_url:      b.logo_url      || '',
          display_name:  b.display_name  || '',
        });
      }
    } catch { /* ignore */ }
  };

  const saveBranding = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(branding).filter(([_, v]) => v !== '' && v != null)
      );
      const res = await fetch(`${API_URL}/api/superadmin/orgs/${selectedId}/branding`, {
        method: 'PUT', headers, body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSavedAt(new Date().toISOString());
        // Apply preview immediately if this org is the one currently logged in
        const myOrg = localStorage.getItem('current_organization_id');
        if (myOrg === selectedId && (payload.primary_color || payload.accent_color)) {
          if (payload.primary_color) document.documentElement.style.setProperty('--brand-primary', payload.primary_color);
          if (payload.accent_color)  document.documentElement.style.setProperty('--brand-accent',  payload.accent_color);
        }
      }
    } finally { setSaving(false); }
  };

  if (forbidden) {
    return (
      <AccessDeniedPage detail="Superadmin access required. Your user record needs `superadmin: true`." />
    );
  }

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="superadmin-page">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-overline text-ink-muted flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" /> Platform
            </div>
            <h1 className="font-display text-3xl text-ink mt-1">Superadmin Console</h1>
            <p className="text-ink-muted text-sm mt-1">All organisations across the platform. Per-tenant theming and config lives here.</p>
          </div>
          <button onClick={loadOrgs} className="px-3 py-2 text-sm border border-surface-border rounded-md hover:bg-surface motion-organic" data-testid="superadmin-refresh">
            {loading ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : null}
            Refresh
          </button>
        </div>

        {error ? (
          <div className="bg-danger/5 border border-danger/30 text-danger rounded-md p-4 text-sm" data-testid="superadmin-error">
            {error}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Org list */}
            <aside className="lg:col-span-4 bg-surface border border-surface-border rounded-lg overflow-hidden" data-testid="superadmin-orgs-list">
              <div className="px-4 py-3 border-b border-surface-border font-display text-sm flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Organisations ({orgs.length})
              </div>
              {loading ? (
                <div className="p-8 text-center text-ink-muted"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
              ) : orgs.length === 0 ? (
                <p className="p-6 text-sm text-ink-muted text-center">No organisations.</p>
              ) : (
                <ul className="max-h-[70vh] overflow-y-auto">
                  {orgs.map(o => (
                    <li key={o.id}>
                      <button
                        onClick={() => openOrg(o.id)}
                        className={`w-full text-left px-4 py-3 border-b border-surface-border last:border-0 hover:bg-surface-sunken motion-organic ${
                          selectedId === o.id ? 'bg-primary-50' : ''
                        }`}
                        data-testid={`superadmin-org-${o.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-ink truncate">{o.name}</div>
                            <div className="text-xs text-ink-muted mt-0.5 flex items-center gap-2">
                              <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {o.counts?.users ?? 0}</span>
                              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.counts?.locations ?? 0}</span>
                              {o.tier ? <span className="px-1.5 py-0.5 bg-surface-sunken rounded text-xs">{o.tier}</span> : null}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-ink-muted" />
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </aside>

            {/* Detail / branding editor */}
            <section className="lg:col-span-8 space-y-4">
              {!selectedId ? (
                <div className="bg-surface border border-surface-border rounded-lg p-12 text-center" data-testid="superadmin-no-org">
                  <Shield className="w-10 h-10 mx-auto text-ink-subtle mb-3" />
                  <p className="font-display text-lg text-ink">Pick an organisation</p>
                  <p className="text-sm text-ink-muted mt-1">Its config and branding will open here.</p>
                </div>
              ) : !detail ? (
                <div className="bg-surface border border-surface-border rounded-lg p-10 text-center">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              ) : (
                <>
                  <div className="bg-surface border border-surface-border rounded-lg p-5" data-testid="superadmin-org-detail">
                    <div className="text-xs uppercase tracking-overline text-ink-muted">Organisation</div>
                    <h2 className="font-display text-2xl text-ink mt-1">{detail.organization.name}</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
                      <Cell label="ID" value={detail.organization.id} mono />
                      <Cell label="Tier" value={detail.organization.tier || detail.organization.plan || '—'} />
                      <Cell label="Users" value={detail.organization?.counts?.users ?? '—'} mono />
                      <Cell label="Locations" value={detail.organization?.counts?.locations ?? '—'} mono />
                    </div>
                  </div>

                  <div className="bg-surface border border-surface-border rounded-lg p-5" data-testid="superadmin-branding-form">
                    <div className="flex items-center gap-2 mb-4">
                      <Palette className="w-4 h-4 text-accent-500" />
                      <h3 className="font-display text-base">Per-tenant theming</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <BrandField label="Display name" testid="brand-display-name"
                        value={branding.display_name}
                        onChange={(v) => setBranding({ ...branding, display_name: v })} />
                      <BrandField label="Logo URL" testid="brand-logo-url"
                        value={branding.logo_url}
                        onChange={(v) => setBranding({ ...branding, logo_url: v })} />
                      <BrandField label="Primary color (hex)" testid="brand-primary-color"
                        placeholder="#1E3A2F"
                        value={branding.primary_color}
                        onChange={(v) => setBranding({ ...branding, primary_color: v })} />
                      <BrandField label="Accent color (hex)" testid="brand-accent-color"
                        placeholder="#D96C4A"
                        value={branding.accent_color}
                        onChange={(v) => setBranding({ ...branding, accent_color: v })} />
                    </div>

                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                      <button
                        onClick={saveBranding} disabled={saving}
                        className="px-4 py-2 bg-primary-500 text-surface rounded-md text-sm hover:bg-primary-600 disabled:opacity-50 motion-organic"
                        data-testid="superadmin-save-branding"
                      >
                        {saving ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : <Save className="w-4 h-4 inline mr-1" />}
                        Save branding
                      </button>
                      {savedAt ? (
                        <span className="text-xs text-ok" data-testid="superadmin-saved-indicator">
                          Saved {new Date(savedAt).toLocaleTimeString()}
                        </span>
                      ) : null}
                      {/* Preview swatches */}
                      {branding.primary_color || branding.accent_color ? (
                        <span className="ml-auto flex items-center gap-2 text-xs text-ink-muted">
                          Preview:
                          {branding.primary_color ? <span className="w-5 h-5 rounded border border-surface-border" style={{ background: branding.primary_color }} /> : null}
                          {branding.accent_color ? <span className="w-5 h-5 rounded border border-surface-border" style={{ background: branding.accent_color }} /> : null}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

const Cell = ({ label, value, mono }) => (
  <div>
    <div className="text-xs uppercase tracking-overline text-ink-muted">{label}</div>
    <div className={`text-sm text-ink ${mono ? 'font-data tabular-nums truncate' : ''}`} title={String(value)}>{value}</div>
  </div>
);

const BrandField = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <label className="text-xs uppercase tracking-overline text-ink-muted">{label}</label>
    <input
      type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="mt-1 w-full px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
      data-testid={testid}
    />
  </div>
);

export default SuperAdminConsole;
